/**
 * Well Servicing AI drafts (WS-6, fieldiq-api#1296): names, skins, who may press
 * what, and how a refusal reads. WT-6's lib/wt-ai.ts, for this vertical.
 *
 * NOTHING HERE COMPUTES A FIGURE. Hours, counts, flags and states are the
 * API's deterministic layer; this file words them. The AI's part is the draft
 * text only, shown as a draft until a person approves it.
 */
import { wsApiError, type WsSkin } from './well-servicing';
import type { WsAiAgent } from '../types/ws-ai';

/** Mirrors fieldiq-api lib/ws-roles.ts WS_AI_ROLES. For hiding, not enforcing. */
export const WS_AI_ROLES = ['admin', 'office_staff', 'supervisor'] as const;
export const canUseWsAi = (role: string | null | undefined): boolean => !!role && (WS_AI_ROLES as readonly string[]).includes(role);

export const WS_AI_AGENT_LABEL: Record<WsAiAgent, string> = {
  ws_tour_summary: 'Tour summary',
  ws_ticket_preflight: 'Preflight',
  ws_well_report_draft: 'Well report draft',
  ws_compliance_digest: 'Compliance digest',
};
export const wsAiAgentLabel = (v: string | null | undefined): string =>
  v && Object.prototype.hasOwnProperty.call(WS_AI_AGENT_LABEL, v) ? WS_AI_AGENT_LABEL[v as WsAiAgent] : '—';

const UNKNOWN: WsSkin = { label: '—', fill: 'bg-fi-signal-slate', rail: 'border-l-transparent' };
const pick = (map: Record<string, WsSkin>, v: string | null | undefined): WsSkin =>
  v && Object.prototype.hasOwnProperty.call(map, v) ? map[v] : UNKNOWN;

/**
 * Review outcome (agent_runs.outcome). A pending draft reads as WAITING ON A
 * PERSON, never as anything published. Unknown → `—`, never a guessed state.
 */
export const WS_AI_OUTCOME_SKIN: Record<string, WsSkin> = {
  pending: { label: 'Awaiting your approval', fill: 'bg-fi-signal-amber', rail: 'border-l-fi-amber' },
  actioned: { label: 'Approved', fill: 'bg-fi-signal-green', rail: 'border-l-fi-green' },
  dismissed: { label: 'Dismissed', fill: 'bg-fi-signal-slate', rail: 'border-l-fi-steel' },
  superseded: { label: 'Superseded', fill: 'bg-fi-signal-slate', rail: 'border-l-fi-steel' },
  no_action: { label: 'No action', fill: 'bg-fi-signal-slate', rail: 'border-l-fi-steel' },
};
export const wsAiOutcomeSkin = (v: string | null | undefined): WsSkin => pick(WS_AI_OUTCOME_SKIN, v);

/**
 * The register's review filter. `approved` is the API's `actioned`. The API's
 * list takes no outcome, so this filters the rows it returned (its latest 20)
 * — the register says so beside the chips.
 */
export const WS_AI_OUTCOME_FILTERS = [
  { key: 'pending', label: 'Pending', outcome: 'pending' },
  { key: 'approved', label: 'Approved', outcome: 'actioned' },
  { key: 'dismissed', label: 'Dismissed', outcome: 'dismissed' },
  { key: 'superseded', label: 'Superseded', outcome: 'superseded' },
] as const;
export type WsAiOutcomeFilter = (typeof WS_AI_OUTCOME_FILTERS)[number]['key'];
export const wsAiOutcomeOf = (filter: string): string | null =>
  WS_AI_OUTCOME_FILTERS.find((f) => f.key === filter)?.outcome ?? null;

/** A compliance item's state (lib/ws-ai-digests.ts STATE_ORDER). */
export const WS_AI_STATE_SKIN: Record<string, WsSkin> = {
  expired: { label: 'Expired', fill: 'bg-fi-signal-red', rail: 'border-l-fi-red' },
  recert_required: { label: 'Recertification required', fill: 'bg-fi-signal-red', rail: 'border-l-fi-red' },
  overdue: { label: 'Overdue', fill: 'bg-fi-signal-red', rail: 'border-l-fi-red' },
  missing: { label: 'Missing', fill: 'bg-fi-signal-red', rail: 'border-l-fi-red' },
  would_fail_on_job: { label: 'Fails the check for the job', fill: 'bg-fi-signal-amber', rail: 'border-l-fi-amber' },
  due_soon: { label: 'Due soon', fill: 'bg-fi-signal-amber', rail: 'border-l-fi-amber' },
};
export const wsAiStateSkin = (v: string | null | undefined): WsSkin => pick(WS_AI_STATE_SKIN, v);

/** What a stored run covered, from its trigger data. Unknown → `—`. */
export function wsAiScopeText(agent: string, triggerData: Record<string, unknown> | null, day: (v: string | null) => string): string {
  const t = triggerData ?? {};
  const str = (k: string) => (typeof t[k] === 'string' && t[k] ? (t[k] as string) : null);
  if (agent === 'ws_tour_summary') {
    const parts = [str('jobNumber'), str('workDate') ? day(str('workDate')) : null].filter((p): p is string => !!p && p !== '—');
    return parts.length ? `Tour sheet · ${parts.join(' · ')}` : '—';
  }
  if (agent === 'ws_ticket_preflight') return str('ticketNumber') ?? (str('jobNumber') ? `${str('jobNumber')} (whole job)` : '—');
  if (agent === 'ws_well_report_draft') return str('jobNumber') ? `Report on ${str('jobNumber')}` : '—';
  if (agent === 'ws_compliance_digest') return str('today') ? `As of ${day(str('today'))}` : '—';
  return '—';
}

/**
 * The statuses a ticket preflight may be offered on. The API refuses a
 * customer-approved ticket (409 ticket_customer_approved) and a void one (409
 * ticket_void). An ALLOWLIST, so a status this file does not know is not
 * offered a run the API might refuse.
 */
export const WS_PREFLIGHTABLE_STATUSES = ['draft', 'office_approved', 'customer_rejected'] as const;
export const wsTicketPreflightable = (status: string | null | undefined): boolean =>
  !!status && (WS_PREFLIGHTABLE_STATUSES as readonly string[]).includes(status);

/** The API's edit limits on approve (routes/well-servicing-ai.routes.ts). Checked there; mirrored here to say so first. */
export const WS_AI_MAX_NARRATIVE = 2000;
export const WS_AI_MAX_OBSERVATIONS = 8;
export const WS_AI_MAX_OBSERVATION = 400;

export const WS_AI_PERMISSION_REFUSAL = 'You do not have permission to do this. AI drafts are for admins, office staff and supervisors.';

/** The 409 that a draft for an issued report earns — on the run and on approve. Said as a sentence, never a code. */
export const WS_REPORT_ISSUED_SENTENCE =
  'This report is already issued, so its narrative cannot change. Draft a summary only on a draft report; dismiss any draft written before it was issued.';

/**
 * A refusal from the AI routes, in the API's own words. These routes answer
 * `{ error, code }` and every `error` is already a sentence; a 403 is a
 * refusal whatever the body says; `report_issued` gets the sentence above.
 */
export function wsAiRefusal(err: unknown, fallback: string): string {
  const e = wsApiError(err);
  if (e.status === 403) return WS_AI_PERMISSION_REFUSAL;
  if (e.code === 'report_issued') return WS_REPORT_ISSUED_SENTENCE;
  if (e.status === 404 && !e.code) return 'That record no longer exists. It may have been deleted.';
  return e.message ?? fallback;
}
